import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocale } from "../i18n/I18nProvider.jsx";
import { useAuth } from "../Auth/AuthProvider.jsx";
import { useHttp } from "../Common/HttpProvider.jsx";
import { WEB_SOCKET_SERVER } from "../../constants.js";
import useEffectWithPreviousValues from "../../hooks/useEffectWithPreviousValues.js";
import { useWindowFocused } from "../../hooks/useWindowFocused.js";

/**
 * @typedef {Object} ChatContext
 * @property {IncomingWsMessage} lastWsIncomingMessage,
 * @property {'DISCONNECTED'|'CONNECTING'|'CONNECTED'} status,
 * @property {MutableRefObject<LastPostedMessage>} lastPostedMessage,
 * @property {MessageData[]} messages,
 * @property {ConversationItem[]} conversations,
 * @property {ConversationItem} currentConversation,
 * @property {ConversationItem[]} filteredConversations,
 * @property {Employee[]} employees,
 * @property {number} unreadConversationsCount,
 * @property {function} setLocation,
 * @property {function(message: string, requestId: string)} sendMessage,
 * @property {function} reconnect,
 * @property {function} close,
 * @property {number} selectedEmployee,
 * @property {function(id: number)} setSelectedEmployee,
 * @property {function(unreadFilter: boolean)} setUnreadFilter,
 * @property {Employee} selectedEmployeeInfo,
 * @property {function(employee: Employee)} setSelectedEmployeeInfo,
 * @property {function(messageId: number)} deleteMessage,
 * @property {function} closeConversation,
 * @property {boolean} loaderAiAnswer
 * @property {function} getEmployees,
 * */

export const WSStatus = Object.freeze({
  DISCONNECTED: "DISCONNECTED",
  CONNECTING: "CONNECTING",
  CONNECTED: "CONNECTED",
});

const reconnectDelay = 1000; // стартовая задержка перед повторным подключением (мс)
const maxReconnectDelay = 30000; // максимальная задержка (мс)
const pingInterval = 0; // интервал «heartbeat»-пингов (мс). 0 = не посылать

const chatContextInitial = {};

/**
 * @type {React.Context<ChatContext|*>}
 */
const chatContext = createContext(chatContextInitial);

/**
 * @returns {ChatContext}
 */
export const useChat = () => useContext(chatContext);

/** */
const ChatProvider = ({ children }) => {
  const { user, subject } = useAuth();
  const { get, authTokenRef, del, put } = useHttp();
  const companyId = user?.companyId;
  const { locale } = useLocale();
  const [lastWsIncomingMessage, setLastWsIncomingMessage] = useState(/** @type IncomingWsMessage */ null);
  const [status, setStatus] = useState(WSStatus.DISCONNECTED);
  const wsRef = useRef(/** @type WebSocket */ null);
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef(0);
  const pingTimer = useRef(0);
  const [selectedEmployee, setSelectedEmployee] = useState(0);
  const [selectedEmployeeInfo, setSelectedEmployeeInfo] = useState(null);
  const lastPostedMessage = useRef(/** @type LastPostedMessage */ null);
  const [messages, setMessages] = useState(/** @type MessageData[] */ []);
  const [conversations, setConversations] = useState(/** @type ConversationItem[] */ []);
  const [filteredConversations, setFilteredConversations] = useState(/** @type ConversationItem[] */ []);
  const [unreadFilter, setUnreadFilter] = useState(false);
  const [location, setLocation] = useState(null);
  const [history, setHistory] = useState(null);
  const isInFocus = useWindowFocused();
  const [unreadConversationsCount, setUnreadConversationsCount] = useState(0);
  const [loaderAiAnswer, setLoaderAiAnswer] = useState(false);
  const [employees, setEmployees] = useState(/** @type Employee[] */ []);

  const locationPathname = location?.pathname;

  const currentConversation = useMemo(() => conversations?.find((conv) => conv.userId === selectedEmployee), [
    conversations,
    selectedEmployee,
  ]);

  const markReadAndUpdateConversations = useCallback(
    async (altMessages) => {
      const lastMessageId = (altMessages || messages).find((msg) => msg.direction === "incoming")?.id;
      if (!lastMessageId) return;
      const convs = await put(`chat/mark-read/${lastMessageId}`, null, null, null, true);
      setConversations(convs);
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [messages],
  );

  // Фильтрация диалогов по непрочитанным
  useEffect(() => {
    if (!conversations) return;
    setFilteredConversations(
      conversations.filter((conv) => conv.userId === selectedEmployee || (unreadFilter ? conv.unreadCount > 0 : true)),
    );
  }, [unreadFilter, conversations, selectedEmployee]);

  // Загрузка истории сообщений с выбранным собеседником
  useEffect(() => {
    setMessages([]); // при смене диалогов очищаем историю сообщений

    /** */
    const request = async () => {
      const data = await get(`chat/history/${selectedEmployee}?limit=50`);

      setMessages(data);

      // === пометить сообщения прочитанными, если есть непрочитанные ===
      if (currentConversation?.unreadCount > 0) {
        markReadAndUpdateConversations(data);
      }
      // ================================================================
    };

    if (selectedEmployee) {
      request().then();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee]);

  // Подсчёт непрочитанных диалогов
  useEffect(() => {
    setUnreadConversationsCount(
      conversations?.reduce((accum, current) => accum + (current.unreadCount > 0 ? 1 : 0), 0) || 0,
    );
  }, [conversations]);

  // Загрузка данных по выбранному учителю
  useEffectWithPreviousValues(
    ([prevSelectedEmployee, prevLastWsIncomingMessage]) => {
      /** */
      const fetchEmployee = async () => {
        const data = await get(`chat/employee/${selectedEmployee}`);
        setSelectedEmployeeInfo(data);
      };

      if (selectedEmployee) {
        if (
          // Загружать данные по учителю только если сменился выбранный учитель или пришло новое уведомление
          // типа "onlineChanged". Последнее нужно, чтобы получить актуальный lastOnline
          prevSelectedEmployee !== selectedEmployee ||
          (prevLastWsIncomingMessage !== lastWsIncomingMessage &&
            lastWsIncomingMessage.type === "onlineChanged" &&
            lastWsIncomingMessage.online === false &&
            lastWsIncomingMessage.userId === selectedEmployee)
        ) {
          fetchEmployee().then();
        }
        // При переключении сбрасываем показ лоадера
        if (prevSelectedEmployee !== selectedEmployee) {
          setLoaderAiAnswer(false);
        }
      } else {
        setSelectedEmployeeInfo(null);
      }
    },
    [selectedEmployee, lastWsIncomingMessage],
  );

  // Обработчик входящих сообщений
  useEffect(() => {
    if (!lastWsIncomingMessage) return;

    // Добавление в чат успешно отправленных сообщений
    if (
      lastWsIncomingMessage.type === "responseOnRequest" &&
      lastWsIncomingMessage.success &&
      lastWsIncomingMessage.requestId === lastPostedMessage.current.requestId &&
      lastPostedMessage.current.employeeId === selectedEmployee
    ) {
      const now = Date.now();
      setMessages([
        {
          id: lastWsIncomingMessage.payload.newMessageId,
          date: now,
          direction: "outgoing",
          text: lastPostedMessage.current.text,
        },
        ...messages,
      ]);
    }

    // Добавление в чат входящего сообщения от собеседника и отметка прочитанности
    if (lastWsIncomingMessage.type === "newMessage" && lastWsIncomingMessage.from === selectedEmployee) {
      const now = Date.now();
      setMessages([
        { id: lastWsIncomingMessage.messageId, date: now, direction: "incoming", text: lastWsIncomingMessage.text },
        ...messages,
      ]);
      // Если общение с ИИ-учителем, то при получении ответа убираем лоадер
      if (selectedEmployeeInfo?.isAiAssistant) {
        setLoaderAiAnswer(false);
      }

      // Если вкладка бразуера в фокусе, то только в этом случае отправить отметку о прочитанности
      if (isInFocus && locationPathname === "/chat") {
        put(`chat/mark-read/${lastWsIncomingMessage.messageId}`, null, null, null, true).then();
      } else {
        // Если не в фокусе, то добавить к unreadCount диалога единицу
        setConversations((convs) => {
          const conv = convs.find((it) => it.userId === selectedEmployee);
          if (!conv) return convs;
          conv.unreadCount++;
          return [...convs];
        });
      }
    }

    // Меняем статус "онлайн/офлайн" в списке диалогов
    if (lastWsIncomingMessage.type === "onlineChanged") {
      const userId = lastWsIncomingMessage.userId;
      const online = lastWsIncomingMessage.online;
      setConversations((prevConvers) => {
        prevConvers.forEach((convers) => {
          if (convers.userId === userId) {
            convers.online = online;
          }
        });
        return [...prevConvers];
      });
    }

    // Удаление сообщений
    if (lastWsIncomingMessage.type === "messageDeleted") {
      if (lastWsIncomingMessage.from === selectedEmployee) {
        // Удаление из текущего чата удалённых собеседником сообщений
        // Отсеиваем входящие удалённые сообщения
        setMessages((msgs) =>
          msgs.filter((msg) => msg.id !== lastWsIncomingMessage.messageId || msg.direction === "outgoing"),
        );
      }

      // Обновляем список диалогов
      getConversations().then();
    }

    // Простановка статуса прочитанности отправленных сообщений
    if (lastWsIncomingMessage.type === "read" && lastWsIncomingMessage.from === selectedEmployee) {
      setMessages((msgs) => {
        msgs.forEach((msg) => {
          if (lastWsIncomingMessage.messages.includes(msg.id)) {
            msg.read = true;
          }
        });
        return [...msgs];
      });
    }

    // Переход из чата на главную страницу при отключении чата
    if (lastWsIncomingMessage.type === "disableChat") {
      setSelectedEmployee(0);
      if (locationPathname.startsWith("/chat")) {
        history.replace("/");
      }
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastWsIncomingMessage]);

  /** @type function(): Promise<void> */
  const getConversations = useCallback(async () => {
    /** @type ConversationItem[]|* */
    const conversations = await get("chat/conversations", null, null, true);
    if (!conversations) return;

    conversations.forEach((it) => (it.date = new Date(it.date)));
    setConversations(conversations);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Начальная загрузка диалогов (когда пользователь первый раз заходит в чат)
  useEffect(() => {
    if (!authTokenRef.current) return;

    if (!lastWsIncomingMessage) {
      getConversations().then();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastWsIncomingMessage, user]);

  // Обновление диалогов при входящих/исходящих сообщениях
  useEffect(() => {
    if (!authTokenRef.current || !lastWsIncomingMessage) return;

    if (
      (lastWsIncomingMessage.type === "newMessage" && lastWsIncomingMessage.from === selectedEmployee) ||
      lastWsIncomingMessage.success
    ) {
      // При входящем или успешном исходящем текстовом сообщении обновляем последнее сообщение в диалогах
      setConversations((convs) => {
        convs.forEach((conv) => {
          if (lastWsIncomingMessage.type === "newMessage" && conv.userId === lastWsIncomingMessage.from) {
            conv.lastMessage = lastWsIncomingMessage.text;
          }

          if (lastWsIncomingMessage.success && conv.userId === selectedEmployee) {
            conv.lastMessage = lastPostedMessage.current.text;
          }
        });
        return [...convs];
      });
    } else {
      // Если текстовое сообщение пришло не в текущий чат, то надо обновить список диалогов, чтобы появился бейджик
      // непрочитанных сообщений
      getConversations().then();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastWsIncomingMessage, user]);

  // Обновление диалогов при смене предмета
  useEffect(() => {
    if (subject?.subjectId) {
      getConversations().then();
    }
  }, [subject?.subjectId, getConversations]);

  // Отслеживание возвращения пользователя в окно браузера, чтобы отметить прочитанным пришедшие в фоне сообщения
  useEffect(() => {
    /** */
    const handleFocus = () => {
      if (locationPathname === "/chat" && currentConversation?.unreadCount > 0) {
        markReadAndUpdateConversations();
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [currentConversation, locationPathname, markReadAndUpdateConversations]);

  // Отслеживание открытия страницы чата, чтобы пометить непрочитанные сообщения прочитанными
  useEffect(() => {
    if (locationPathname === "/chat" && currentConversation?.unreadCount > 0) {
      markReadAndUpdateConversations();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationPathname]);

  // Очистка массивов с сообщениями и диалогами при смене пользователя
  useEffect(() => {
    setMessages([]);
    setConversations([]);
    setSelectedEmployee(0);
  }, [user]);

  /* ---------- helpers ---------- */
  /** */
  const clearTimers = () => {
    clearTimeout(reconnectTimeout.current);
    clearInterval(pingTimer.current);
  };

  /* ---------- build URL ---------- */
  const buildConnectionUrl = useCallback(() => {
    const token = authTokenRef.current;
    if (!token) throw new Error("auth token is required");

    const url = `${WEB_SOCKET_SERVER}/chat/user`;
    return `${url}?authorization=${encodeURIComponent(token)}&company-id=${companyId}&locale=${locale}`;
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, locale]);

  /* ---------- connect ---------- */
  const connect = useCallback(() => {
    if (!companyId || wsRef.current || !authTokenRef.current) return;

    console.log("Socket connecting...");

    /** */
    const scheduleReconnect = () => {
      reconnectAttempts.current += 1;
      const delay = Math.min(reconnectDelay * 2 ** (reconnectAttempts.current - 1), maxReconnectDelay);
      wsRef.current = null;
      reconnectTimeout.current = setTimeout(connect, delay);
    };

    clearTimers();
    setStatus(WSStatus.CONNECTING);

    const finalUrl = buildConnectionUrl();
    const ws = new WebSocket(finalUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus(WSStatus.CONNECTED);
      reconnectAttempts.current = 0;

      console.log("Socket connected!");

      if (pingInterval > 0) {
        ws.send(JSON.stringify({ type: "ping" }));
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, pingInterval);
      }
    };

    ws.onmessage = (e) => {
      const incomingData = JSON.parse(e.data);
      console.log("ws incoming:", incomingData);
      setLastWsIncomingMessage(incomingData);
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
    };

    ws.onclose = () => {
      setStatus(WSStatus.DISCONNECTED);
      scheduleReconnect();
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, buildConnectionUrl]);

  /* ---------- send ---------- */
  const sendMessage = useCallback(
    (message, requestId) => {
      if (!selectedEmployee || !message) return;

      const ws = wsRef.current;
      const msgPackage = JSON.stringify({
        to: selectedEmployee,
        text: message,
        requestId,
      });

      if (ws?.readyState === WebSocket.OPEN) {
        lastPostedMessage.current = { requestId, employeeId: selectedEmployee, text: message };
        ws.send(msgPackage);
        // Если чат с ИИ-учителем при отправке сообщения включаем лоадер ожидания ответа
        if (selectedEmployeeInfo?.isAiAssistant) {
          setLoaderAiAnswer(true);
        }
      } else {
        console.warn("WebSocket закрыт, сообщение не отправлено");
      }
    },
    [selectedEmployee, selectedEmployeeInfo],
  );

  /* ---------- close ---------- */
  const close = useCallback(() => {
    clearTimers();
    const ws = wsRef.current;

    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.close();
      console.log("Socket disconnected.");
    }
  }, []);

  /* ---------- lifecycle ---------- */
  useEffect(() => {
    connect();
    return () => close();
  }, [connect, close]);

  const deleteMessage = useCallback(
    async (messageId) => {
      const resp = await del(`chat/message/${messageId}`);
      if (!resp) return;
      // Отсеиваем исходящие удалённые сообщения
      setMessages((msgs) => msgs.filter((msg) => msg.id !== messageId || msg.direction === "incoming"));
      // Обновляем список диалогов
      await getConversations();
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [getConversations],
  );

  const closeConversation = useCallback(async () => {
    if (!currentConversation?.id) return;

    const conversations = await put(`chat/close-conversation/${currentConversation.id}`);
    setConversations(conversations);
    setSelectedEmployee(0);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversation?.id]);

  const getEmployees = useCallback(async () => {
    const resp = await get("chat/employees");
    if (!resp) return;
    setEmployees(resp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    lastWsIncomingMessage,
    status,
    lastPostedMessage,
    messages,
    conversations,
    currentConversation,
    filteredConversations,
    unreadConversationsCount,
    employees,
    setLocation,
    setHistory,
    sendMessage,
    reconnect: connect,
    close,
    selectedEmployee,
    setSelectedEmployee,
    selectedEmployeeInfo,
    setSelectedEmployeeInfo,
    setUnreadFilter,
    deleteMessage,
    getConversations,
    closeConversation,
    loaderAiAnswer,
    getEmployees,
  };

  return <chatContext.Provider value={value}>{children}</chatContext.Provider>;
};

export default ChatProvider;
